import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@contexts/LanguageContext';
import { Button } from '@components/common';
import Icon from '@components/Icon/Icon';
import { useAuthorization } from '@hooks/useAuthorization';
import { ActionType, ModelType } from '@constants/authorization';
import './ViewPageHeader.scss';

/**
 * Common header component for view pages with centralized permission management
 *
 * @param {Object} props
 * @param {Function} props.onBack - Back button handler (optional, will use navigate(-1) if not provided)
 * @param {Function} props.onEdit - Edit button handler
 * @param {Function} props.onDelete - Delete button handler
 * @param {string} props.backPath - Custom back path (optional)
 * @param {boolean} props.showEdit - Whether to show edit button (default: true) - overrides permission check
 * @param {boolean} props.showDelete - Whether to show delete button (default: true) - overrides permission check
 * @param {boolean} props.disableDelete - Whether to disable delete button
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Additional header content
 *
 * Centralized permission props (optional - if provided, will check permissions automatically):
 * @param {string} props.modelType - Model type for permission check (e.g., 'RESOURCE', 'BORROWING')
 * @param {string|number} props.modelId - Model ID for permission check
 * @param {Object} props.context - Additional context for permission check
 * @param {Object} props.permissions - Pre-computed permissions object (alternative to modelType/modelId)
 * @param {boolean} props.permissions.canUpdate - Can perform update action
 * @param {boolean} props.permissions.canDelete - Can perform delete action
 * @param {boolean} props.permissionsLoading - Whether permissions are being loaded
 */
const ViewPageHeader = ({
    onBack,
    onEdit,
    onDelete,
    backPath,
    showEdit,
    showDelete,
    disableDelete = false,
    className = '',
    children,
    // Centralized permission props
    modelType,
    modelId,
    context = {},
    permissions,
    permissionsLoading = false
}) => {
    const navigate = useNavigate();
    const { t } = useLanguage();

    // Check permissions if modelType is provided and no pre-computed permissions
    const { canPerform: canUpdate, loading: updateLoading } = useAuthorization(
        (modelType && !permissions) ? ActionType.UPDATE : null,
        modelType,
        modelId,
        context
    );

    const { canPerform: canDelete, loading: deleteLoading } = useAuthorization(
        (modelType && !permissions) ? ActionType.DELETE : null,
        modelType,
        modelId,
        context
    );

    // Use pre-computed permissions if available, otherwise use authorization hook results
    const finalCanUpdate = permissions ? permissions.canUpdate : canUpdate;
    const finalCanDelete = permissions ? permissions.canDelete : canDelete;
    const finalLoading = permissions ? permissionsLoading : (updateLoading || deleteLoading);

    // Determine if buttons should be shown
    // If showEdit/showDelete are explicitly provided, use them
    // Otherwise, use permission check results
    const shouldShowEdit = showEdit !== undefined ? showEdit : ((modelType || permissions) ? finalCanUpdate : true);
    const shouldShowDelete = showDelete !== undefined ? showDelete : ((modelType || permissions) ? finalCanDelete : true);

    // Show loading state if checking permissions
    const isLoading = (modelType || permissions) && finalLoading;

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        }
        else if (onBack) {
            onBack();
        } else if (backPath) {
            navigate(backPath);
        }
    };

    return (
        <div className={`view-page-header ${className}`}>
            <div className="header-actions">
                <div className="action-buttons">
                    {isLoading && (
                        <div className="loading-permissions">
                            <span className="loading-spinner"></span>
                            Vérification des permissions...
                        </div>
                    )}

                    {!isLoading && shouldShowEdit && onEdit && (
                        <Button
                            variant="primary"
                            onClick={onEdit}
                        >
                            <Icon icon="edit" />
                            <span>{t('common.edit')}</span>
                        </Button>
                    )}

                    {!isLoading && shouldShowDelete && onDelete && (
                        <Button
                            variant="danger"
                            onClick={onDelete}
                            disabled={disableDelete}
                        >
                            <Icon icon="trash" />
                            <span>{t('common.delete')}</span>
                        </Button>
                    )}
                    {children}
                </div>
            </div>
        </div>
    );
};

export default ViewPageHeader;
